import { ChevronRight, Package, Ship, Weight } from "lucide-react"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { StageChip } from "@/features/transit/components/stage-chip"
import type { OTListItem } from "@/features/transit/api/types"
import { paths } from "@/routes/paths"

interface TransitRowProps {
  ot: OTListItem
}

export function TransitRow({ ot }: TransitRowProps) {
  const { t } = useTranslation("transit")
  const navigate = useNavigate()
  const vesselText = [ot.vessel, ot.voyage].filter(Boolean).join(" · ")

  return (
    <button
      type="button"
      onClick={() => navigate(paths.transitOrderDetail(ot.id))}
      className="bg-card hover:bg-muted/40 group flex w-full items-center gap-4 rounded-md border px-4 py-3 text-left transition-colors"
    >
      {/* Identity column — client primary, OT number + contract muted */}
      <div className="min-w-0 flex-[1_1_280px]">
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-foreground cursor-help truncate text-sm font-semibold">
                {ot.clientName}
              </span>
            </TooltipTrigger>
            <TooltipContent>{t("list.row.clientHint")}</TooltipContent>
          </Tooltip>
          <StageChip stage={ot.status} archived={ot.archived} />
        </div>
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs tabular-nums">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help font-mono">{ot.otNumber}</span>
            </TooltipTrigger>
            <TooltipContent>{t("list.row.otHint")}</TooltipContent>
          </Tooltip>
          <Separator />
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help font-mono">
                {ot.contractNumber ?? `#${ot.contractId}`}
              </span>
            </TooltipTrigger>
            <TooltipContent>{t("list.row.contractHint")}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Stats column — each stat shows a label + value, icons reinforce.
          The labels are the source of truth; icons help quick recognition. */}
      <div className="hidden min-w-0 flex-shrink-0 items-center gap-5 md:flex">
        <Stat
          icon={Package}
          tooltip={t("list.row.lotsHint")}
          label={t("list.row.lotsLabel", { count: ot.lotCount })}
          value={ot.lotCount.toString()}
        />
        <Stat
          icon={Weight}
          tooltip={t("list.row.tonnageHint")}
          label={t("list.row.tonnageLabel")}
          value={
            <Trans
              i18nKey="transit:list.row.tonnageValue"
              values={{ mt: ot.totalTonnageMT.toFixed(2) }}
              components={{
                mt: <span className="text-foreground font-semibold" />,
                u: <span className="text-muted-foreground" />,
              }}
            />
          }
        />
        {vesselText ? (
          <Stat
            icon={Ship}
            tooltip={t("list.row.vesselHint")}
            label={t("list.row.vesselLabel")}
            value={vesselText}
            className="hidden max-w-[220px] lg:inline-flex"
          />
        ) : null}
      </div>

      <ChevronRight className="text-muted-foreground/40 group-hover:text-muted-foreground size-4 flex-shrink-0 transition-colors" />
    </button>
  )
}

function Separator() {
  return (
    <span aria-hidden className="text-muted-foreground/40">
      ·
    </span>
  )
}

function Stat({
  icon: Icon,
  tooltip,
  label,
  value,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>
  tooltip: string
  label: string
  value: React.ReactNode
  className?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`text-muted-foreground inline-flex cursor-help items-center gap-2 text-xs tabular-nums ${className ?? ""}`}
        >
          <Icon className="size-3.5 flex-shrink-0" />
          <span className="flex flex-col leading-tight">
            <span className="text-[10px] font-medium uppercase tracking-wide">
              {label}
            </span>
            <span className="text-foreground truncate text-sm font-medium">
              {value}
            </span>
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
